import type { UnitAccess, UnitBasics } from '@clockwise/clients/business-units';
import { ApiError, lookupPermission, type SystemRolePermissionId } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { businessUnitsApi } from './api';
import { useMe } from './me';
import { useHasPermission } from './permissions';
import { queryKeys } from './query-keys';

/** How a person reaches a unit, in words. */
export const UNIT_ACCESS_LABEL: Record<UnitAccess, string> = {
  primary: 'Primary employer',
  granted: 'Granted access',
  'all-units': 'All Business Units',
};

/** "Northwind (NORTH)". */
export function unitLabel(unit: Pick<UnitBasics, 'name' | 'code'>): string {
  return `${unit.name} (${unit.code})`;
}

/** The message to show for a failed Business Unit operation: the first
 *  field error when there is one (it names what to fix), else the error. */
export function unitErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.fields?.[0]?.message ?? err.message;
  return err instanceof Error ? err.message : fallback;
}

/**
 * Whether the caller manages Business Units — adds and edits them, grants and
 * removes access, changes a primary employer: they hold `business_unit.edit`.
 * The server decides; this keeps controls it would refuse out of sight.
 */
export function useCanManageUnits(): boolean {
  return useHasPermission('business_unit.edit');
}

/**
 * Whether the caller may add a Business Unit: they hold BOTH
 * `business_unit.edit` and `business_unit.all_units` (a new unit is reached
 * by nobody else yet).
 */
export function useCanCreateUnits(): boolean {
  const canEdit = useHasPermission('business_unit.edit');
  const allUnits = useHasPermission('business_unit.all_units');
  return canEdit && allUnits;
}

/**
 * Whether the caller may give someone a role, or take it away: they hold
 * every RESTRICTED permission it carries (the server's rule — see
 * `restricted-permissions.ts`). Controls the server would refuse stay hidden.
 */
export function useCanHandleRole(): (
  role: { permissions: readonly { permission: string }[] } | undefined,
) => boolean {
  const { permissions } = useMe();
  return (role) =>
    !!role &&
    role.permissions.every(
      (p) => !lookupPermission(p.permission)?.restricted || permissions.has(p.permission),
    );
}

/**
 * The units the caller can see, by name: every unit for someone who can open
 * Setup → Business Units (the server narrows it to their own units unless
 * they manage units), else the units they work in.
 */
export function useVisibleUnits(): UnitBasics[] {
  const canView = useHasPermission('business_unit.view');
  const { businessUnits } = useMe();
  const list = useQuery({
    queryKey: queryKeys.businessUnits.names(),
    queryFn: () => businessUnitsApi.names(),
    enabled: canView,
  });
  return canView && list.data ? list.data.data : businessUnits;
}

/**
 * The caller's role in each unit they work in, as `unit id → permission id →
 * scope` (`undefined` for a scopeless permission). From `/users/me`. Empty
 * while loading.
 */
export function useUnitPermissions(): ReadonlyMap<string, ReadonlyMap<string, string | undefined>> {
  const { businessUnits } = useMe();
  return useMemo(
    () =>
      new Map(
        businessUnits.map((u) => [
          u.id,
          new Map((u.permissions ?? []).map((e) => [e.permission, e.scope])),
        ]),
      ),
    [businessUnits],
  );
}

/**
 * `(permission, unitId) → whether the caller's role IN THAT UNIT holds it` —
 * for a record that belongs to a unit (a customer, a role, an employee's
 * employer), so a screen offers what that record's unit allows: the server
 * judges every record there (docs/business-units.md → "Route gates"). With
 * `unrestricted`, only at scope All (or scopeless). A unit they do not work in
 * holds nothing. Before `/me` has loaded, or with no unit given, the caller's
 * overall permissions decide.
 */
export function useCanIn(): (
  permission: SystemRolePermissionId,
  unitId: string | null | undefined,
  opts?: { unrestricted?: boolean },
) => boolean {
  const byUnit = useUnitPermissions();
  const { permissions } = useMe();
  return (permission, unitId, opts) => {
    if (!unitId || byUnit.size === 0) return permissions.has(permission);
    const held = byUnit.get(unitId);
    if (!held?.has(permission)) return false;
    if (!opts?.unrestricted) return true;
    const scope = held.get(permission);
    return scope == null || scope === 'all';
  };
}

/**
 * The units (of those the caller can see) where their role holds
 * `permission` — e.g. where they may add a customer (`customer.edit`) or an
 * employee (`user.edit`, `unrestricted`).
 */
export function useUnitsWhere(
  permission: SystemRolePermissionId,
  opts?: { unrestricted?: boolean },
): UnitBasics[] {
  const units = useVisibleUnits();
  const canIn = useCanIn();
  return units.filter((u) => canIn(permission, u.id, opts));
}

/**
 * Whether Business Units are worth showing on a screen: the caller sees that
 * screen's records (`permission`, its view permission) in more than one unit.
 * Someone who works in two units but sees customers in only one of them gets
 * no unit column, filter or picker on Customers. A single-unit tenant sees
 * none anywhere, exactly as before units existed.
 */
export function useShowsUnits(permission: SystemRolePermissionId): boolean {
  return useUnitsWhere(permission).length > 1;
}

/** The caller's units' names by id — for a record's `businessUnitId`. Every
 *  customer and role the caller can see belongs to one of their units. */
export function useUnitNames(): ReadonlyMap<string, string> {
  const units = useVisibleUnits();
  return useMemo(() => new Map(units.map((u) => [u.id, u.name])), [units]);
}
